import React, { useState } from 'react';

import { faPlugCircleXmark, faMobileScreen } from '@fortawesome/pro-regular-svg-icons';

import confirm from 'components/confirm';
import { Button } from 'components/ui/button';
import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { PhoneEnrollmentDialog } from './dialog';
import { deleteRequest } from 'helpers/api';
import { showSnackbar } from 'components/ui/snackbar/helper';

import type { EnrollmentStep } from './dialog';
import type { PhoneEnrollment, UserProfilePaths } from 'components/users/types';

interface Props {
  enrollment: PhoneEnrollment | null;
  paths: UserProfilePaths;
  personalNumber: string | null;
  onChange: (enrollment: PhoneEnrollment | null) => void;
}

export function PhoneEnrollmentSection({ enrollment, paths, personalNumber, onChange }: Props) {
  const [step, setStep] = useState<EnrollmentStep | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  async function disconnect() {
    try {
      await confirm('Are you sure you want to stop texting the assistant?', {
        buttonLabel: 'Disconnect',
        buttonStyle: 'danger',
        title: 'Disconnect texting',
      });
    } catch {
      return;
    }

    try {
      setDisconnecting(true);
      await deleteRequest(paths.phoneEnrollment);
      onChange(null);
    } catch {
      showSnackbar({ text: 'Something went wrong.', variant: 'danger' });
    } finally {
      setDisconnecting(false);
    }
  }

  // Only a verified enrollment closes the dialog; a pending one is lifted so reopening the section
  // lands back on the code step instead of a create that the server will reject as already set up.
  function handleChange(next: PhoneEnrollment) {
    if (next.verificationStatus === 'verified') {
      setStep(null);
    }

    onChange(next);
  }

  const verified = enrollment?.verificationStatus === 'verified';
  const awaitingCode = enrollment?.verificationStatus === 'pending';
  const optedOut = enrollment?.optedOut === true;

  function description() {
    if (optedOut) {
      const number = enrollment.assistantNumber;
      return `You replied STOP, so the assistant can't text you. Text START to ${number} to turn it back on.`;
    }

    if (verified) {
      return 'Text this number to reach the assistant from your phone.';
    }

    return 'Reach the assistant by text message from your own phone.';
  }

  return (
    <Field className="form-group">
      <Field.Label>Text the assistant</Field.Label>

      {verified ? (
        <div className="d-flex align-items-center gap-2">
          <span>{enrollment.assistantNumber}</span>
          <Button
            disabled={disconnecting}
            icon={faPlugCircleXmark}
            label="Disconnect"
            variant="ghost"
            onClick={disconnect}
          />
        </div>
      ) : (
        <div className="d-flex align-items-center gap-2">
          <Button
            icon={faMobileScreen}
            label={awaitingCode ? 'Enter code' : 'Set up texting'}
            variant="outline"
            onClick={() => setStep(awaitingCode ? 'code' : 'number')}
          />
          {awaitingCode && <span className="text-muted">Waiting for confirmation</span>}
        </div>
      )}

      <Field.Description>{description()}</Field.Description>

      {step && (
        <PhoneEnrollmentDialog
          initialStep={step}
          paths={paths}
          personalNumber={enrollment?.personalNumber || personalNumber}
          onChange={handleChange}
          onClose={() => setStep(null)}
        />
      )}
    </Field>
  );
}
